import './DataGrid.scss';
import React, { useMemo } from 'react';
import Button from '../Button/Button';
import Select from '../Select/Select';

function DataGrid({
  rows = [],
  columns = [],
  pageSizeOptions = [10],
  paginationModel = { page: 0, pageSize: pageSizeOptions[0] || 10 },
  onPaginationModelChange,
  getRowId = (row) => row.id,
  onRowClick,
  className = '',
  disablePagination = false,
  renderAsTable = true,
}) {
  const gridClassName = ['custom-data-grid', className].filter(Boolean).join(' ');
  const page = paginationModel.page || 0;
  const pageSize = paginationModel.pageSize || pageSizeOptions[0] || 10;
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);

  const visibleRows = useMemo(() => {
    if (disablePagination) {
      return rows;
    }

    const start = safePage * pageSize;
    return rows.slice(start, start + pageSize);
  }, [disablePagination, rows, safePage, pageSize]);

  const updatePagination = (nextModel) => {
    if (onPaginationModelChange) {
      onPaginationModelChange(nextModel);
    }
  };

  const handlePageSizeChange = (event) => {
    updatePagination({ page: 0, pageSize: Number(event.target.value) });
  };

  const handlePreviousPage = () => {
    updatePagination({ page: Math.max(0, safePage - 1), pageSize });
  };

  const handleNextPage = () => {
    updatePagination({ page: Math.min(pageCount - 1, safePage + 1), pageSize });
  };

  const renderCell = (row, column, rowIndex) => {
    const id = getRowId(row);
    const value = row[column.field];
    const params = { id, row, field: column.field, value, rowIndex };

    if (column.renderCell) {
      return column.renderCell(params);
    }

    return value ?? '-';
  };

  const getColumnWidth = (column) => {
    if (column.width) {
      return column.width;
    }

    return Math.max(column.minWidth || 120, (column.flex || 1) * 160);
  };

  const getColumnTrack = (column) => {
    const width = getColumnWidth(column);

    return typeof width === 'number' ? `${width}px` : width;
  };

  const gridTemplateColumns = columns.map(getColumnTrack).join(' ');
  const numericGridWidth = columns.reduce((total, column) => {
    const width = getColumnWidth(column);

    return typeof width === 'number' ? total + width : total;
  }, 0);
  const divGridStyle = numericGridWidth
    ? { minWidth: `${numericGridWidth}px` }
    : undefined;
  const divRowStyle = { gridTemplateColumns };

  const renderDivGrid = () => (
    <div className="custom-data-grid__layout custom-data-grid__layout--div" style={divGridStyle}>
      <div className="custom-data-grid__head" role="rowgroup">
        <div className="custom-data-grid__row custom-data-grid__row--header" style={divRowStyle} role="row">
          {columns.map((column) => (
            <div
              key={column.field}
              className="custom-data-grid__header"
              style={{
                minWidth: column.minWidth,
                width: getColumnWidth(column),
              }}
              role="columnheader"
            >
              <span className="custom-data-grid__header-title">{column.headerName || column.field}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="custom-data-grid__body" role="rowgroup">
        {visibleRows.map((row, rowIndex) => {
          const id = getRowId(row);

          return (
            <div
              key={id}
              className="custom-data-grid__row"
              style={divRowStyle}
              onClick={() => onRowClick && onRowClick({ id, row })}
              role="row"
            >
              {columns.map((column) => (
                <div
                  key={`${id}-${column.field}`}
                  className="custom-data-grid__cell"
                  style={{
                    minWidth: column.minWidth,
                    width: getColumnWidth(column),
                  }}
                  role="cell"
                >
                  {renderCell(row, column, rowIndex)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderTableGrid = () => (
    <table className="custom-data-grid__table">
      <colgroup>
        {columns.map((column) => (
          <col
            key={column.field}
            style={{
              minWidth: column.minWidth,
              width: getColumnWidth(column),
            }}
          />
        ))}
      </colgroup>
      <thead className="custom-data-grid__head">
        <tr className="custom-data-grid__row custom-data-grid__row--header">
          {columns.map((column) => (
            <th
              key={column.field}
              className="custom-data-grid__header"
              style={{
                minWidth: column.minWidth,
                width: getColumnWidth(column),
              }}
              scope="col"
            >
              <span className="custom-data-grid__header-title">{column.headerName || column.field}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="custom-data-grid__body">
        {visibleRows.map((row, rowIndex) => {
          const id = getRowId(row);

          return (
            <tr
              key={id}
              className="custom-data-grid__row"
              onClick={() => onRowClick && onRowClick({ id, row })}
            >
              {columns.map((column) => (
                <td
                  key={`${id}-${column.field}`}
                  className="custom-data-grid__cell"
                  style={{
                    minWidth: column.minWidth,
                    width: getColumnWidth(column),
                  }}
                >
                  {renderCell(row, column, rowIndex)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className={gridClassName}>
      <div className="custom-data-grid__viewport">
        {renderAsTable ? renderTableGrid() : renderDivGrid()}
      </div>

      {!disablePagination && (
        <div className="custom-data-grid__footer">
          <Select
            className="custom-data-grid__page-size"
            label="Lignes par page"
            size="small"
            value={pageSize}
            onChange={handlePageSizeChange}
            options={pageSizeOptions.map((option) => ({ value: option, label: String(option) }))}
          />

          <div className="custom-data-grid__pagination">
            <span>
              Page {safePage + 1} / {pageCount}
            </span>
            <Button size="small" variant="tertiary" onClick={handlePreviousPage} disabled={safePage === 0}>
              Precedent
            </Button>
            <Button size="small" variant="tertiary" onClick={handleNextPage} disabled={safePage >= pageCount - 1}>
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataGrid;
